import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { requestPasswordResetWithLambda, resetPasswordWithLambda } from '../repository/authRepository';

export const PasswordReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    const result = await requestPasswordResetWithLambda(username);
    if ('error' in result) {
      setMessage(result.error);
      setIsError(true);
    } else {
      setMessage('Password reset email sent! Check your email.');
      setIsError(false);
      setUsername('');
    }
    setIsLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      setIsError(true);
      return;
    }
    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters');
      setIsError(true);
      return;
    }
    if (!resetToken) {
      setMessage('No reset token provided');
      setIsError(true);
      return;
    }

    setIsLoading(true);
    const result = await resetPasswordWithLambda(resetToken, newPassword);
    if ('error' in result) {
      setMessage(result.error);
      setIsError(true);
    } else {
      setMessage('Password updated! Redirecting to login...');
      setIsError(false);
      setTimeout(() => navigate('/login'), 2000);
    }
    setIsLoading(false);
  };

  if (resetToken) {
    return (
      <div className="auth-container">
        <div className="card auth-card">
          <h2 className="auth-title">Reset Your Password</h2>
          {message && <div className={`alert ${isError ? 'alert-error' : 'alert-success'}`}>{message}</div>}
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input type="password" className="form-input" value={newPassword}
                onChange={e => setNewPassword(e.target.value)} required minLength={8} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input type="password" className="form-input" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)} required minLength={8} />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="card auth-card">
        <h2 className="auth-title">Reset Your Password</h2>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '16px', fontSize: '0.875rem' }}>
          Enter your username and we'll send you a reset link.
        </p>
        {message && <div className={`alert ${isError ? 'alert-error' : 'alert-success'}`}>{message}</div>}
        <form onSubmit={handleRequestReset}>
          <div className="form-group">
            <label className="form-label">Username</label>
            <input type="text" className="form-input" value={username}
              onChange={e => setUsername(e.target.value)} required />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        <div className="auth-footer">
          <Link to="/login">Back to Login</Link>
        </div>
      </div>
    </div>
  );
};
